import React, { useState } from 'react'
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Menu as MenuIcon,
  Notifications,
  AccountCircle,
  Logout,
  Settings,
} from '@mui/icons-material'
import { useAppSelector, useAppDispatch } from '../../hooks/redux'
import { logout } from '../../store/slices/authSlice'
import { toggleSidebar } from '../../store/slices/uiSlice'
import { Sidebar } from './Sidebar'
import { BottomNavigation } from './BottomNavigation'
import { NotificationPanel } from './NotificationPanel'
import { useNotifications } from '../../hooks/useWebSocket'

const DRAWER_WIDTH = 320 // Increased for better Material Design 3 spacing

interface LayoutProps {
  children: React.ReactNode
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'))
  const dispatch = useAppDispatch()
  
  const { user } = useAppSelector((state) => state.auth)
  const { sidebarOpen } = useAppSelector((state) => state.ui)
  const { notifications, unreadCount } = useNotifications()
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null)

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget)
  }

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null)
  }

  const handleLogout = () => {
    dispatch(logout())
    handleProfileMenuClose()
  }

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar())
  }

  const drawerContent = <Sidebar />

  return (
    <Box sx={{ display: 'flex', backgroundColor: 'background.default' }}>
      {/* Material Design 3 Top App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: isMobile
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            : 'surface',
          color: isMobile ? '#ffffff' : 'onSurface',
          borderBottom: 'none',
          borderRadius: 0,
          backdropFilter: isMobile ? 'blur(10px)' : 'none',
          boxShadow: isMobile
            ? '0 4px 20px rgba(102, 126, 234, 0.15)'
            : '0 1px 3px rgba(0,0,0,0.12)',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          ...(sidebarOpen && !isMobile && {
            marginLeft: DRAWER_WIDTH,
            width: `calc(100% - ${DRAWER_WIDTH}px)`,
            transition: theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }),
        }}
      >
        <Toolbar sx={{ minHeight: { xs: '56px', sm: '64px' }, px: { xs: 2, sm: 3 } }}>
          {!isMobile && (
            <IconButton
              color="inherit"
              aria-label="toggle drawer"
              onClick={handleToggleSidebar}
              edge="start"
              size={isMobile ? 'medium' : 'large'}
              sx={{
                mr: { xs: 1, sm: 2, md: 3 },
                borderRadius: 2,
                transition: theme.transitions.create(['background-color', 'transform'], {
                  duration: theme.transitions.duration.short,
                }),
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {isMobile ? (
            <Box
              component="img"
              src="/logo.png"
              alt="GF System"
              sx={{
                height: 13,
                width: 'auto',
                objectFit: 'contain',
                mr: 'auto',
              }}
            />
          ) : (
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                flexGrow: 1,
                fontWeight: 500,
                color: 'onSurface',
                letterSpacing: '0px',
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
              }}
            >
              Garment Factory Management
            </Typography>
          )}

          {/* Notifications */}
          <Tooltip title="Notifications" placement="bottom">
            <IconButton
              color="inherit"
              onClick={handleNotificationMenuOpen}
              aria-label={`show ${unreadCount} new notifications`}
              size={isMobile ? 'medium' : 'large'}
              sx={{
                mr: { xs: 1, sm: 2 },
                borderRadius: 2,
                transition: theme.transitions.create(['background-color', 'transform'], {
                  duration: theme.transitions.duration.short,
                }),
                '&:hover': {
                  backgroundColor: isMobile
                    ? 'rgba(255, 255, 255, 0.15)'
                    : 'action.hover',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              <Badge
                badgeContent={unreadCount}
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: isMobile ? '#ff4757' : 'error.main',
                    color: '#ffffff',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    minWidth: 18,
                    height: 18,
                    border: isMobile ? '2px solid rgba(255, 255, 255, 0.3)' : 'none',
                  },
                }}
              >
                <Notifications sx={{ fontSize: isMobile ? 22 : 24 }} />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Profile Menu */}
          <Tooltip title="Account settings" placement="bottom">
            <IconButton
              size={isMobile ? 'medium' : 'large'}
              edge="end"
              aria-label="account of current user"
              aria-controls="primary-search-account-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
              sx={{
                borderRadius: 2,
                transition: theme.transitions.create(['background-color', 'transform'], {
                  duration: theme.transitions.duration.short,
                }),
                '&:hover': {
                  backgroundColor: isMobile
                    ? 'rgba(255, 255, 255, 0.15)'
                    : 'action.hover',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              <Avatar
                sx={{
                  width: { xs: 34, sm: 40 },
                  height: { xs: 34, sm: 40 },
                  backgroundColor: isMobile ? '#ffffff' : 'primary.main',
                  color: isMobile ? '#667eea' : 'onPrimary',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: 600,
                  border: isMobile ? '2px solid rgba(255, 255, 255, 0.3)' : '2px solid',
                  borderColor: isMobile ? 'rgba(255, 255, 255, 0.3)' : 'outline',
                  boxShadow: isMobile ? '0 2px 8px rgba(0, 0, 0, 0.15)' : 'none',
                }}
                alt={user?.full_name || 'User'}
                src="/static/images/avatar/1.jpg"
              >
                {user?.full_name?.charAt(0) || <AccountCircle />}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Material Design 3 Navigation Drawer - Hidden on Mobile */}
      {!isMobile && (
        <Box
          component="nav"
          sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
        >
          <Drawer
            variant="persistent"
            open={sidebarOpen}
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: DRAWER_WIDTH,
                backgroundColor: 'surfaceContainerLow',
                borderRight: 'none',
                borderRadius: '0 16px 16px 0',
              },
            }}
          >
            {drawerContent}
          </Drawer>
        </Box>
      )}

      {/* Material Design 3 Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          pb: { xs: 10, sm: 3 },
          backgroundColor: 'background.default',
          minHeight: '100vh',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: '56px', sm: '64px' } }} />
        {children}
      </Box>

      {/* Bottom Navigation for Mobile */}
      {isMobile && <BottomNavigation />}

      {/* Material Design 3 Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        id="primary-search-account-menu"
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: 'surfaceContainer',
            borderRadius: 2,
            marginTop: 1,
            minWidth: 240,
            py: 1,
          },
        }}
      >
        <MenuItem
          onClick={handleProfileMenuClose}
          sx={{
            borderRadius: 1,
            mx: 1,
            my: 0.5,
            px: 2,
            py: 1.5,
            minHeight: 56,
            transition: theme.transitions.create('background-color', {
              duration: theme.transitions.duration.short,
            }),
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <Avatar
            sx={{
              mr: 2,
              width: 32,
              height: 32,
              backgroundColor: 'primary.main',
              color: 'onPrimary',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            {user?.full_name?.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500} color="onSurface">
              {user?.full_name}
            </Typography>
            <Typography variant="caption" color="onSurfaceVariant">
              {user?.role}
            </Typography>
          </Box>
        </MenuItem>
        <MenuItem
          onClick={handleProfileMenuClose}
          sx={{
            borderRadius: 1,
            mx: 1,
            my: 0.5,
            px: 2,
            py: 1.5,
            minHeight: 48,
            transition: theme.transitions.create('background-color', {
              duration: theme.transitions.duration.short,
            }),
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              borderRadius: 1,
              backgroundColor: 'surfaceContainerHigh',
              mr: 2,
            }}
          >
            <Settings sx={{ fontSize: 16, color: 'onSurfaceVariant' }} />
          </Box>
          <Typography variant="body2" color="onSurface">
            Settings
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={handleLogout}
          sx={{
            borderRadius: 1,
            mx: 1,
            my: 0.5,
            px: 2,
            py: 1.5,
            minHeight: 48,
            transition: theme.transitions.create('background-color', {
              duration: theme.transitions.duration.short,
            }),
            '&:hover': {
              backgroundColor: 'errorContainer',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              borderRadius: 1,
              backgroundColor: 'errorContainer',
              mr: 2,
            }}
          >
            <Logout sx={{ fontSize: 16, color: 'onErrorContainer' }} />
          </Box>
          <Typography variant="body2" color="error.main">
            Logout
          </Typography>
        </MenuItem>
      </Menu>

      {/* Notification Panel */}
      <NotificationPanel
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationMenuClose}
        notifications={notifications}
      />
    </Box>
  )
}
